/**
 * LanguageSwitcher Component
 *
 * Switches the published route locale by writing the canonical `?lang=`
 * query parameter consumed by the public Custom App controller.
 */
import type { ChangeEvent, MouseEvent } from 'react';
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import * as styles from '../styles.css';

const configSchema = z.object({
  display: z.enum(['buttons', 'select']).default('buttons'),
  uppercaseLabels: z.boolean().default(true),
});

interface LocaleConfig {
  availableLocales: string[];
  currentLocale: string;
  defaultLocale: string;
}

function LanguageSwitcherRenderer({
  config,
  mode,
  runtimeContext,
}: ViewComponentRenderProps) {
  const { display, uppercaseLabels } = config as z.infer<typeof configSchema>;
  const localeConfig = readLocaleConfig(runtimeContext);
  const isBuilder = mode === 'builder';
  const isPreview = mode === 'preview';

  if (localeConfig.availableLocales.length <= 1 && mode === 'public') {
    return null;
  }

  if (display === 'select') {
    return createElement(
      'label',
      {
        style: {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--sl-custom-app-gap-xs)',
          fontSize: 'var(--sl-custom-app-font-size-sm)',
          color: 'var(--streamlain-text-secondary-color)',
        },
      },
      createElement(
        'span',
        { style: { position: 'absolute', opacity: 0, pointerEvents: 'none' } },
        'Language'
      ),
      createElement(
        'select',
        {
          value: localeConfig.currentLocale,
          disabled: isBuilder,
          onChange: (event: ChangeEvent<HTMLSelectElement>) => {
            if (isBuilder) return;
            if (isPreview) {
              runtimeContext?.onLocaleChange?.(event.target.value);
              return;
            }
            navigateToLocale(event.target.value);
          },
          style: {
            minHeight: 32,
            padding: '4px 28px 4px 10px',
            border: '1px solid var(--streamlain-border-color)',
            borderRadius: 'var(--sl-custom-app-radius-sm)',
            background: 'var(--streamlain-background-primary-color)',
            color: 'var(--streamlain-text-primary-color)',
            fontSize: 'var(--sl-custom-app-font-size-sm)',
          },
          'aria-label': 'Language',
        },
        ...localeConfig.availableLocales.map(locale =>
          createElement(
            'option',
            { key: locale, value: locale },
            formatLocaleLabel(locale, uppercaseLabels)
          )
        )
      )
    );
  }

  return createElement(
    'nav',
    {
      'aria-label': 'Language',
      style: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 'var(--sl-custom-app-gap-xs)',
      },
    },
    ...localeConfig.availableLocales.map(locale => {
      const active = locale === localeConfig.currentLocale;
      return createElement(
        'a',
        {
          key: locale,
          href: isBuilder ? '#' : createLocaleHref(locale),
          onClick: (event: MouseEvent<HTMLAnchorElement>) => {
            if (isBuilder) {
              event.preventDefault();
              return;
            }
            if (isPreview) {
              event.preventDefault();
              runtimeContext?.onLocaleChange?.(locale);
            }
          },
          'aria-current': active ? 'true' : undefined,
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 30,
            minWidth: 36,
            padding: '4px 10px',
            border: '1px solid var(--streamlain-border-color)',
            borderRadius: 'var(--sl-custom-app-radius-sm)',
            background: active
              ? 'var(--streamlain-primary-color)'
              : 'var(--streamlain-background-primary-color)',
            color: active
              ? 'var(--streamlain-background-primary-color)'
              : 'var(--streamlain-text-primary-color)',
            fontSize: 'var(--sl-custom-app-font-size-sm)',
            fontWeight: active ? 600 : 500,
            textDecoration: 'none',
            opacity: isBuilder && !active ? 0.72 : 1,
          },
        },
        formatLocaleLabel(locale, uppercaseLabels)
      );
    })
  );
}

function readLocaleConfig(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): LocaleConfig {
  const customApp = runtimeContext?.customApp;
  const customAppConfig =
    customApp && typeof customApp === 'object' && 'config' in customApp
      ? ((customApp as { config?: unknown }).config as
          | Record<string, unknown>
          | undefined)
      : null;
  const availableFromCustomApp = Array.isArray(
    customAppConfig?.['availableLocales']
  )
    ? customAppConfig['availableLocales']
    : Array.isArray(customApp?.['availableLocales'])
      ? customApp['availableLocales']
      : null;
  const availableLocales =
    availableFromCustomApp
      ?.filter((locale): locale is string => typeof locale === 'string')
      .filter((locale, index, all) => all.indexOf(locale) === index) ?? [];
  const defaultLocale =
    (typeof customAppConfig?.['defaultLocale'] === 'string'
      ? customAppConfig['defaultLocale']
      : typeof customApp?.['defaultLocale'] === 'string'
        ? customApp['defaultLocale']
        : runtimeContext?.locale) ?? 'en';
  const currentLocale = runtimeContext?.locale ?? defaultLocale;
  const normalizedLocales = availableLocales.includes(currentLocale)
    ? availableLocales
    : [currentLocale, ...availableLocales];
  return {
    availableLocales: normalizedLocales.length > 0 ? normalizedLocales : ['en'],
    currentLocale,
    defaultLocale,
  };
}

function formatLocaleLabel(locale: string, uppercase: boolean): string {
  return uppercase ? locale.toUpperCase() : locale;
}

function createLocaleHref(locale: string): string {
  if (typeof window === 'undefined')
    return `?lang=${encodeURIComponent(locale)}`;
  const url = new URL(window.location.href);
  url.searchParams.set('lang', locale);
  return `${url.pathname}${url.search}${url.hash}`;
}

function navigateToLocale(locale: string): void {
  if (typeof window === 'undefined') return;
  const anchor = window.document.createElement('a');
  anchor.href = createLocaleHref(locale);
  anchor.hidden = true;
  window.document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

export const LanguageSwitcherComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.LANGUAGE_SWITCHER,
  type: 'navigation',
  name: 'Language Switcher',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    'A/文'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    display: 'buttons',
    uppercaseLabels: true,
  },
  render(props) {
    return createElement(LanguageSwitcherRenderer, props);
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      'A/文 Language'
    );
  },
};
